import { Calendar, ChevronRight, Clock, MapPin, User } from 'lucide-react'
import { Body, Caption } from '@meiuca/ui-components'
import { formatDateForUI, parseAPIDate } from '@meiuca/web-foundation'
import { formatLevel, formatTimeRange } from '@/lib/trainingCenterLabels'
import { useComponentImpression } from '@meiuca/analytics'
import { COMPONENT_TYPES } from '@/constants/analyticsEvents'
import type { WorkoutListItem } from '@/services/workoutService'

interface WorkoutCardProps {
  workout: WorkoutListItem
  onClick?: () => void
  position?: number
  /**
   * Rótulo "Aluno: <nome>" — só quando a conta enxerga mais de um aluno
   * (responsável com dependentes); itens de dependente sempre rotulados.
   */
  showStudent?: boolean
}

export function WorkoutCard({ workout, onClick, position, showStudent = false }: WorkoutCardProps) {
  const impressionRef = useComponentImpression({
    componentType: COMPONENT_TYPES.WORKOUT_CARD,
    componentKey: `workout_${workout.id}`,
    position,
  })
  const interactive = Boolean(onClick)
  const level = formatLevel(workout.level ?? workout.trainingClass?.level)
  const isCanceled = workout.status === 'canceled'
  const participantCanceled = workout.customerParticipant?.status === 'cancelled'

  return (
    <div
      ref={impressionRef}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      className={`rounded-lg bg-background-white border border-border-light p-4 transition-shadow hover:shadow-md ${
        interactive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-primary/40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Body className="text-base font-semibold text-text-primary mb-1">
            {workout.trainingCenter.name}
          </Body>
          <Caption className="text-text-secondary flex items-start gap-1">
            <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-1">{workout.location.name}</span>
          </Caption>
          {(showStudent || workout.isDependent) && workout.studentCustomer && (
            <Caption className="text-text-secondary flex items-center gap-1 mt-1">
              <User className="w-3 h-3 flex-shrink-0" />
              <span>Aluno: {workout.studentCustomer.name}</span>
            </Caption>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {(isCanceled || participantCanceled) && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-feedback-error/15 text-feedback-error">
              Cancelado
            </span>
          )}
          {level && (
            <span className="px-2 py-1 rounded text-xs font-medium bg-brand-primary/10 text-brand-primary">
              {level}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-text-secondary mt-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          <Body className="text-sm">{formatDateForUI(parseAPIDate(workout.date), 'long')}</Body>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <Body className="text-sm">
            {formatTimeRange(workout.startTimeMin, workout.endTimeMin)}
          </Body>
        </div>
        {interactive && <ChevronRight className="w-5 h-5 text-text-tertiary ml-auto" />}
      </div>
    </div>
  )
}
